import { deleteObject, ErrorCallbackFunction, getObject, IStatusResponse, postObject, postVoid, putJson, putObject } from "../ChoiceApi"
import { NoTzDate } from '../DateUtils';

const controllerRoute: string = "api/IdMe/V2/";

export async function getProvider(user_id: string): Promise<UserProviderResponse | undefined>
{
    return await postObject<any>(controllerRoute + "GetProvider", { user_id });
}

export async function saveProvider(provider_entity_id: number, user_id: string): Promise<UserProviderResponse | undefined>
{
    return await postObject(controllerRoute + "SaveProvider", {provider_entity_id, user_id});
}

export async function deleteProvider(provider_entity_id: number, user_id: string): Promise<UserProviderResponse | undefined>
{
    return await deleteObject(controllerRoute + `DeleteProvider/${provider_entity_id}/${user_id}`);
}

export async function confirmProvider(provider_npi: string): Promise<IStatusResponse | undefined>
{
    return await putObject<IStatusResponse>(controllerRoute + `Confirm/${provider_npi}`,);
}

// Return a string if there is an error disconnecting the provider
export async function disconnectProvider(provider_entity_id:number, provider_npi: string, user_id: string, reason: string): Promise<string|undefined>
{
    let errorMsg: string|undefined;

    await postVoid(controllerRoute + "Deactivate",
        {
            provider_entity_id, 
            provider_npi, 
            user_id, 
            reason
        }, async (resp) => {
            errorMsg = await resp.text()
        });
    return errorMsg;
}

export async function verifyActiveCredentials(securityUserId:number, npi: string, scopeType: IdMeScopeType): Promise<IdMeVerifyUserResponse|undefined>
{
    const resp = await getObject<IdMeVerifyUserResponse>(controllerRoute + `Mfa/VerifyActiveCredentials/${securityUserId}/${npi}/${scopeType}`);
    return resp;
}

// Get the MFA setups for EPCS
export async function getMfaSetupsForEPCS(errorCallback?: ErrorCallbackFunction) : Promise<MfaSetupModel[] | undefined>
{
    const resp = await getObject<MfaSetupModel[]>(controllerRoute + "Mfa/Setups/EPCS", errorCallback);
    return resp;    
}

// Start an MFA event.  Follow this with checkMfaEventStatus() or VerifyCode code calls depending on the setup type
export async function startMfaEvent(uuidIdMeSetup: string): Promise<IdMeMfaEventResponse|undefined>
{
    const resp = await getObject<IdMeMfaEventResponse>(controllerRoute + `Mfa/StartEvent/${uuidIdMeSetup}`);
    return resp;    
}

// Check on the status of 'push' event
export async function checkMfaEventStatus(uuidIdMeEvent: string): Promise<IdMeMfaEventResponse|undefined>
{
    const resp = await getObject<IdMeMfaEventResponse>(controllerRoute + `Mfa/CheckStatus/${uuidIdMeEvent}`);
    return resp;    
}

// Submit a auth code for 'generator' event
export async function checkMfaEventCode(uuidIdMeEvent: string, code: string): Promise<IdMeMfaEventResponse|undefined>
{
    const resp = await getObject<IdMeMfaEventResponse>(controllerRoute + `Mfa/VerifyCode/${uuidIdMeEvent}/${code}`);
    return resp;    
}


export interface UserProviderResponse
{
    currentProviderSearchCompInfo?: ProviderSearchCompInfo;
    verifyIdentityApiUrl?: string;
    verifyEPCSApiUrl?: string;
    idmeProviderInfo?: IDMEProviderInformation;
    allowChanges: boolean,
    choiceDeaNumbersMatchIdMe: boolean
}

interface ProviderSearchCompInfo
{
    providerEntityId: string;
    billingProviderId: string;
    entityName: string;
}

interface IDMEProviderInformation
{
    idMe?: IdMeProviderInfoModal;
    choiceProvider?: ChoiceProviderInfoModal;
    api?: ApiProviderInfoModel;
    choiceDeaNumbers?: ChoiceDeaNumber[]
}

interface IdMeProviderInfoModal {
    npiConfirmed: boolean;
    npiConfirmDate?: NoTzDate,
    npiConfirmUser?: string,
    npi?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    state?: string;
    userId?: string;
    verifiedDate?: string;
    deaNumbers?: IdMeDeaNumberInfo[]
}

export interface IdMeDeaNumberInfo {
    state: string,  // The state abbreviation
    number: string, // DEA number
    expires_at: NoTzDate
}

interface ChoiceProviderInfoModal {
    npi?: string,
    email?: string,
    firstName?: string,
    lastName?: string,
    state?: string,
    entityId?: number,
    prescribers_active: boolean
}

interface ApiProviderInfoModel {
    providerNpi?: string,
    securityUsersId: number,
    usersId?: string
}

interface ChoiceDeaNumber {
    deaNumber: string,
    deaState: string,
    spiEnabled: boolean
}

interface IdMeVerifyUserResponse {
    verified: boolean
};

export enum IdMeScopeType
{
    VerifyIdentity=1,
    VerifyEPCS=2
}

export enum MfaSetupOptionTypes {
    push = "push",
    phone = "phone",
    generator = "generator",
    fidokey = "fidokey",
    yubikey = "yubikey"
}

export interface MfaSetupModel {
    uuid: string,
    name: string, 
    label: string, 
    option: MfaSetupOptionTypes
}

export enum MfaEventStatusTypes {
    delivering = "delivering",
    waiting = "waiting",
    failed = "failed",
    accepted = "accepted"
}

export interface IdMeMfaEventResponse {
    uuid: string,
    status: MfaEventStatusTypes, 
    expires: string
}